/**
 * Semantic diff between two tutor versions.
 *
 * Deliberately NOT a text diff. A prompt whose pin moves from v1 to v2 is one
 * semantic row ("cm-inactivity-1: v1 -> v2"), and any text differ would render it as
 * a deleted line plus an added line — strictly worse information. Config objects are
 * compared key-by-key against a canonical form so that "absent" and "null" do not
 * read as a change.
 *
 * Pure functions, no React, no network — unit-testable.
 */

import type { PromptSnapshot, TutorVersion } from "@/services/api.service";

export type PromptChangeKind = "added" | "removed" | "repinned" | "modified";

export interface PromptChange {
  prompt_id: string;
  kind: PromptChangeKind;
  fromVersion?: number;
  toVersion?: number;
  /** Fields other than `version` that differ (model, llm_config_name, slot_assignments). */
  changedFields: string[];
}

export interface FieldChange {
  path: string;
  from: unknown;
  to: unknown;
}

export interface TutorVersionDiff {
  prompts: PromptChange[];
  unchangedPromptCount: number;
  config: FieldChange[];
  metadata: FieldChange[];
  isEmpty: boolean;
}

/**
 * Treat undefined, null, "" and [] as the same "nothing here" value.
 *
 * Without this, a TV saved before a field existed diffs against one saved after as a
 * change on every single field, which buries the real edits.
 */
function isBlank(v: unknown): boolean {
  if (v === undefined || v === null || v === "") return true;
  if (Array.isArray(v)) return v.length === 0;
  // An empty object is "nothing here" too: a TV saved with `stt_config: {}`
  // must not diff against one with no stt_config at all.
  if (typeof v === "object") return Object.keys(v as object).length === 0;
  return false;
}

/** Stable deep comparison with key order ignored and blanks normalised. */
export function deepEqual(a: unknown, b: unknown): boolean {
  if (isBlank(a) && isBlank(b)) return true;
  if (a === b) return true;
  if (typeof a !== typeof b) return false;
  if (a === null || b === null) return false;

  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b)) return false;
    if (a.length !== b.length) return false;
    return a.every((item, i) => deepEqual(item, b[i]));
  }

  if (typeof a === "object") {
    const ao = a as Record<string, unknown>;
    const bo = b as Record<string, unknown>;
    // Union of keys, minus those blank on both sides.
    const keys = new Set([...Object.keys(ao), ...Object.keys(bo)]);
    for (const k of keys) {
      if (isBlank(ao[k]) && isBlank(bo[k])) continue;
      if (!deepEqual(ao[k], bo[k])) return false;
    }
    return true;
  }

  return false;
}

const PROMPT_COMPARE_FIELDS = [
  "model",
  "llm_config_name",
  "slot_assignments",
] as const;

function diffPrompts(
  from: PromptSnapshot[],
  to: PromptSnapshot[],
): { changes: PromptChange[]; unchanged: number } {
  // Key by prompt_id, never by array index — the pin list is unordered, and index
  // keying turns a reorder into a wall of false changes.
  const fromMap = new Map(from.map((p) => [p.prompt_id, p]));
  const toMap = new Map(to.map((p) => [p.prompt_id, p]));
  const changes: PromptChange[] = [];
  let unchanged = 0;

  for (const [id, toPrompt] of toMap) {
    const fromPrompt = fromMap.get(id);
    if (!fromPrompt) {
      changes.push({
        prompt_id: id,
        kind: "added",
        toVersion: Number(toPrompt.version),
        changedFields: [],
      });
      continue;
    }

    const versionChanged =
      Number(fromPrompt.version) !== Number(toPrompt.version);
    const changedFields = PROMPT_COMPARE_FIELDS.filter(
      (f) =>
        !deepEqual(
          (fromPrompt as unknown as Record<string, unknown>)[f],
          (toPrompt as unknown as Record<string, unknown>)[f],
        ),
    );

    if (!versionChanged && changedFields.length === 0) {
      unchanged++;
      continue;
    }

    changes.push({
      prompt_id: id,
      kind: versionChanged ? "repinned" : "modified",
      fromVersion: Number(fromPrompt.version),
      toVersion: Number(toPrompt.version),
      changedFields,
    });
  }

  for (const [id, fromPrompt] of fromMap) {
    if (!toMap.has(id)) {
      changes.push({
        prompt_id: id,
        kind: "removed",
        fromVersion: Number(fromPrompt.version),
        changedFields: [],
      });
    }
  }

  const order: Record<PromptChangeKind, number> = {
    repinned: 0,
    modified: 1,
    added: 2,
    removed: 3,
  };
  changes.sort(
    (a, b) =>
      order[a.kind] - order[b.kind] || a.prompt_id.localeCompare(b.prompt_id),
  );

  return { changes, unchanged };
}

const CONFIG_FIELDS = [
  "llm_config",
  "stt_config",
  "tts_config",
] as const;

const METADATA_FIELDS = [
  "name",
  "description",
  "status",
  "org",
  "is_default",
  "created_by",
] as const;

function diffFields(
  from: TutorVersion,
  to: TutorVersion,
  fields: readonly string[],
): FieldChange[] {
  const out: FieldChange[] = [];
  for (const path of fields) {
    const a = (from as unknown as Record<string, unknown>)[path];
    const b = (to as unknown as Record<string, unknown>)[path];
    if (!deepEqual(a, b)) out.push({ path, from: a, to: b });
  }
  return out;
}

// Keyed by name, one row per entry: a reorder is no change, and a notepad model swap reads
// as `llm_configs.notepad` rather than the whole list.
function diffNamedConfigs(from: TutorVersion, to: TutorVersion): FieldChange[] {
  const fromMap = new Map((from.llm_configs ?? []).map((c) => [c.name, c.config]));
  const toMap = new Map((to.llm_configs ?? []).map((c) => [c.name, c.config]));
  const names = [...new Set([...fromMap.keys(), ...toMap.keys()])];
  return names
    .filter((name) => !deepEqual(fromMap.get(name), toMap.get(name)))
    .map((name) => ({ path: `llm_configs.${name}`, from: fromMap.get(name), to: toMap.get(name) }));
}

export function diffTutorVersions(
  from: TutorVersion,
  to: TutorVersion,
): TutorVersionDiff {
  const { changes, unchanged } = diffPrompts(from.prompts ?? [], to.prompts ?? []);
  const config = [...diffFields(from, to, CONFIG_FIELDS), ...diffNamedConfigs(from, to)];
  const metadata = diffFields(from, to, METADATA_FIELDS);

  return {
    prompts: changes,
    unchangedPromptCount: unchanged,
    config,
    metadata,
    isEmpty: changes.length === 0 && config.length === 0 && metadata.length === 0,
  };
}

/** Render a value for display in the diff table. */
export function formatValue(v: unknown): string {
  if (v === undefined) return "—";
  if (v === null) return "null";
  if (typeof v === "string") return v || '""';
  if (typeof v === "object") return JSON.stringify(v, null, 2);
  return String(v);
}
